import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../src/pages/app/protected/firebase';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const adminUIDs = import.meta.env.VITE_ADMIN_UIDS?.split(',') || [];

  const fetchUsers = async () => {
    try {
      const listUsers = httpsCallable(functions, 'listUsers');
      const res = await listUsers();
      setUsers(res.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      alert("Unable to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDisableToggle = async (uid, disabled) => {
    const fn = httpsCallable(functions, 'setUserStatus');
    await fn({ uid, disabled });
    fetchUsers();
  };

  const handleDeleteUser = async (uid) => {
    if (!window.confirm('Are you sure you want to delete this user and all data?')) return;
  
    try {
      const deleteFn = httpsCallable(functions, 'deleteUserAndData');
      await deleteFn({ uid });
      alert('User and data deleted successfully');
      setUsers(prev => prev.filter((u) => u.uid !== uid));
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Error deleting user.');
    }
  };

  const handleSendReset = async (email) => {
    const fn = httpsCallable(functions, 'sendResetPassword');
    const res = await fn({ email });
    alert(`Reset link sent. Link: ${res.data.link}`);
  };

  const adminUsers = users
    .filter((u) => adminUIDs.includes(u.uid))
    .filter((u) => u.email.toLowerCase().includes(searchTerm.toLowerCase()));

  const normalUsers = users
    .filter((u) => !adminUIDs.includes(u.uid))
    .filter((u) => u.email.toLowerCase().includes(searchTerm.toLowerCase()));

  const renderTable = (label, data) => (
    <div className="mb-8">
      <h2 className="text-xl font-bold mb-2">{label}</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded shadow text-sm">
          <thead>
            <tr className="bg-gray-200 text-left">
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Created</th>
              <th className="px-4 py-2">Last Login</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((user) => (
              <tr key={user.uid} className="border-t">
                <td className="px-4 py-2">{user.email}</td>
                <td className="px-4 py-2">
                  {user.disabled ? (
                    <span className="text-red-500">Disabled</span>
                  ) : (
                    <span className="text-green-600">Active</span>
                  )}
                </td>
                <td className="px-4 py-2">{new Date(user.creationTime).toLocaleDateString()}</td>
                <td className="px-4 py-2">
                  {user.lastSignInTime
                    ? new Date(user.lastSignInTime).toLocaleString()
                    : 'Never'}
                </td>
                <td className="px-4 py-2 space-x-2">
                  <button
                    onClick={() => handleDisableToggle(user.uid, !user.disabled)}
                    className="bg-yellow-500 text-white px-2 py-1 rounded"
                  >
                    {user.disabled ? 'Enable' : 'Disable'}
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.uid)}
                    className="bg-red-600 text-white px-2 py-1 rounded"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => handleSendReset(user.email)}
                    className="bg-blue-600 text-white px-2 py-1 rounded"
                  >
                    Reset Password
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">User Management</h1>

      <input
        type="text"
        placeholder="Search by email"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="mb-6 px-4 py-2 border rounded w-full max-w-md"
      />

      {loading ? <p>Loading users...</p> : (
        <>
          {renderTable('Admins', adminUsers)}
          {renderTable('Users', normalUsers)}
        </>
      )}
    </div>
    
  );
}